import { Threshold, CurrentStateForEditor, ItemWeight } from './reducer';
import {
    QuestionAnswerKeyCorrection,
    DraftAssignments, DraftTypes, GradingWorkflowSettingsType, QuestionAssignments, Question,
} from './types';

export enum TypeKeys {
    THRESHOLDS_DATA_LOADED = 'thresholdEditor/THRESHOLDS_DATA_LOADED',
    INITIAL_THRESHOLDS_LOADED = 'thresholdEditor/INITIAL_THRESHOLDS_LOADED',
    THRESHOLD_VALUE_CHANGE = 'thresholdEditor/THRESHOLD_VALUE_CHANGE',
    WORKFLOW_OVERLAY_LOADED = 'workflowSettings/SETTINGS_OVERLAY_LOADED',
    UPDATE_CURRENT_EDITOR_STATE = 'workflowSettings/UPDATE_CURRENT_EDITOR_STATE',
    SET_CURRENT_DRAFT_DETAILS = 'workflowSettings/SET_CURRENT_DRAFT_DETAILS',
    ACQUIRE_LOCK = 'workflowSettings/GET_LOCK',
    ITEM_WEIGHT_CHANGE = 'workflowSettings/ITEM_WEIGHT_CHANGE',
    ITEM_WEIGHT_DATA_LOADED = 'workflowSettings/ITEM_WEIGHT_DATA_LOADED',
    RELEASE_LOCK = 'workflowSettings/RELEASE_LOCK',
    RESET_DRAFT = 'workflowSettings/RESET_DRAFT',
    APPLY_DRAFT = 'workflowSettings/APPLY_DRAFT',
    APPLY_DRAFT_FINISHED = 'workflowSettings/APPLY_DRAFT_FINISHED',
    RELOAD_DRAFT = 'workflowSettings/RELOAD_DRAFT',
    QUESTION_ASSIGNMENT_CHANGE = 'questionAssignment/QUESTION_ASSIGNMENT_CHANGE',
    QUESTION_ASSIGNMENT_DATA_LOADED = 'questionAssignment/QUESTION_ASSIGNMENT_DATA_LOADED',
    QUESTION_ANSWER_KEY_CORRECTION_CHANGE = 'questionAnswerKey/QUESTION_ANSWER_KEY_CORRECTION_CHANGE',
    QUESTION_ANSWER_KEY_CORRECTION_LOADED = 'questionAnswerKey/QUESTION_ANSWER_KEY_CORRECTION_LOADED',
    LOAD_QUESTIONS_FOR_CORRECTION = 'questionAnswerKey/LOAD_QUESTIONS_FOR_CORRECTION',
    QUESTIONS_FOR_CORRECTION_LOADED = 'questionAnswerKey/QUESTIONS_FOR_CORRECTION_LOADED',
    SELECT_QUESTION_FOR_ANSWER_KEY_CORRECTION = 'questionAnswerKey/SELECT_QUESTION_FOR_ANSWER_KEY_CORRECTION',
}

export type ActionTypes = ThresholdsDataLoadedAction | OnThresholdValueChangeAction
    | OnWorkflowSettingsTabLoaded | OnUpdateCurrentEditorState | OnSetCurrentDraftAction | OnAcquireLockAction
    | OnItemWeightChangeAction | OnReleaseLockAction | OnResetDraftAction
    | OnApplyDraftAction | OnApplyDraftFinishedAction | OnReloadDraftAction | InitialThresholdsLoadedAction
    | OnQuestionAssignmentChangeAction | OnQuestionAssignmentDataLoadedAction | OnItemWeightDataLoadedAction
    | OnQuestionAnswerKeyChangeAction | OnQuestionAnswerKeyLoadedAction
    | OnLoadQuestionsForAnswerKey | OnQuestionsForAnswerKeyLoaded | OnSelectQuestionForAnswerKeyCorrection;

export interface OnReloadDraftAction {
    type: TypeKeys.RELOAD_DRAFT;
    draftId: number;
    draftType: DraftTypes;
    draftDeleted?: boolean;
    featureId: string;
}

export interface OnApplyDraftFinishedAction {
    type: TypeKeys.APPLY_DRAFT_FINISHED;
    featureId: string;
}

export interface OnApplyDraftAction {
    type: TypeKeys.APPLY_DRAFT;
    reapplyThresholdsOnly: boolean;
    reviewComponentCandidatesListFeatureId: string;
    featureId: string;
}

export interface OnResetDraftAction {
    type: TypeKeys.RESET_DRAFT;
    featureId: string;
    draftType: DraftTypes;
}

export interface OnReleaseLockAction {
    type: TypeKeys.RELEASE_LOCK;
    featureId: string;
}

export interface OnAcquireLockAction {
    type: TypeKeys.ACQUIRE_LOCK;
    featureId: string;
}

export interface OnSetCurrentDraftAction {
    type: TypeKeys.SET_CURRENT_DRAFT_DETAILS;
    featureId: string;
    details: any;
}

export interface OnUpdateCurrentEditorState {
    type: TypeKeys.UPDATE_CURRENT_EDITOR_STATE;
    featureId: string;
    data: CurrentStateForEditor;
}

export interface OnWorkflowSettingsTabLoaded {
    type: TypeKeys.WORKFLOW_OVERLAY_LOADED;
    featureId: string;
    tabIndex: number;
    tabsGroup: GradingWorkflowSettingsType;
}

export interface OnThresholdValueChangeAction {
    type: TypeKeys.THRESHOLD_VALUE_CHANGE;
    featureId: string;
    newValue: number;
    thresholdId: string;
}

export interface OnItemWeightChangeAction {
    type: TypeKeys.ITEM_WEIGHT_CHANGE;
    featureId: string;
    newWeight: number;
    objectId?: number;
}

export interface OnItemWeightDataLoadedAction {
    type: TypeKeys.ITEM_WEIGHT_DATA_LOADED;
    featureId: string;
    itemWeights: ItemWeight[];
}

export interface ThresholdsDataLoadedAction {
    type: TypeKeys.THRESHOLDS_DATA_LOADED;
    featureId: string;
    data: Threshold[];
    thresholdsXml: string;
}

export interface InitialThresholdsLoadedAction {
    type: TypeKeys.INITIAL_THRESHOLDS_LOADED;
    featureId: string;
    initialThresholds: Threshold[];
}

export interface OnQuestionAssignmentChangeAction {
    type: TypeKeys.QUESTION_ASSIGNMENT_CHANGE;
    featureId: string;
    updatedAssignments: QuestionAssignments[];
}

export interface OnQuestionAssignmentDataLoadedAction {
    type: TypeKeys.QUESTION_ASSIGNMENT_DATA_LOADED;
    featureId: string;
    questionsAssignments: DraftAssignments;
}

export interface OnQuestionAnswerKeyChangeAction {
    type: TypeKeys.QUESTION_ANSWER_KEY_CORRECTION_CHANGE;
    featureId: string;
    updatedAnswerKey: QuestionAnswerKeyCorrection;
}

export interface OnQuestionAnswerKeyLoadedAction {
    type: TypeKeys.QUESTION_ANSWER_KEY_CORRECTION_LOADED;
    featureId: string;
    questionsAnswerKeys: QuestionAnswerKeyCorrection[];
}

export interface OnLoadQuestionsForAnswerKey {
    type: TypeKeys.LOAD_QUESTIONS_FOR_CORRECTION;
    featureId: string;
}

export interface OnQuestionsForAnswerKeyLoaded {
    type: TypeKeys.QUESTIONS_FOR_CORRECTION_LOADED;
    featureId: string;
    questions: Question[];
}

export interface OnSelectQuestionForAnswerKeyCorrection {
    type: TypeKeys.SELECT_QUESTION_FOR_ANSWER_KEY_CORRECTION;
    featureId: string;
    questionId: number;
}

export const reloadDraft = (featureId: string,
    draftId: number,
    draftType: DraftTypes,
    draftDeleted?: boolean): OnReloadDraftAction => ({
    featureId,
    draftId,
    draftType,
    draftDeleted,
    type: TypeKeys.RELOAD_DRAFT,
});

export const applyDraftFinished = (featureId: string): OnApplyDraftFinishedAction => ({
    featureId,
    type: TypeKeys.APPLY_DRAFT_FINISHED,
});

export const applyDraft = (
    featureId: string,
    reapplyThresholdsOnly: boolean,
    reviewComponentCandidatesListFeatureId: string
): OnApplyDraftAction => ({
    featureId,
    reapplyThresholdsOnly,
    reviewComponentCandidatesListFeatureId,
    type: TypeKeys.APPLY_DRAFT,
});

export const resetDraft = (featureId: string, draftType: DraftTypes): OnResetDraftAction => ({
    type: TypeKeys.RESET_DRAFT,
    featureId,
    draftType,
});

export const releaseLock = (featureId: string): OnReleaseLockAction => ({
    featureId,
    type: TypeKeys.RELEASE_LOCK,
});

export const acquireLock = (featureId: string): OnAcquireLockAction => ({
    featureId,
    type: TypeKeys.ACQUIRE_LOCK,
});

export const onItemWeightChange = (featureId: string, newWeight: number, objectId: number): OnItemWeightChangeAction => ({
    featureId,
    newWeight,
    objectId,
    type: TypeKeys.ITEM_WEIGHT_CHANGE,
});

export const itemWeightsDataLoaded = (featureId: string, itemWeights: ItemWeight[]): OnItemWeightDataLoadedAction => ({
    type: TypeKeys.ITEM_WEIGHT_DATA_LOADED,
    featureId,
    itemWeights,
});

export const onThresholdValueChange = (featureId: string, newValue: number, thresholdId: string): OnThresholdValueChangeAction => ({
    featureId,
    newValue,
    thresholdId,
    type: TypeKeys.THRESHOLD_VALUE_CHANGE,
});

export const thresholdsDataLoaded = (featureId: string, data: Threshold[], thresholdsXml?: string): ThresholdsDataLoadedAction => ({
    type: TypeKeys.THRESHOLDS_DATA_LOADED,
    featureId,
    data,
    thresholdsXml,
});

export const initialThresholdsLoaded = (featureId: string, initialThresholds: Threshold[]): InitialThresholdsLoadedAction => ({
    type: TypeKeys.INITIAL_THRESHOLDS_LOADED,
    featureId,
    initialThresholds,
});

export const setCurrentDraft = (featureId: string, details: any): OnSetCurrentDraftAction => ({
    details,
    featureId,
    type: TypeKeys.SET_CURRENT_DRAFT_DETAILS,
});

export const workflowSettingsTabLoaded = (
    featureId: string,
    tabIndex: number,
    tabsGroup: GradingWorkflowSettingsType
): OnWorkflowSettingsTabLoaded => ({
    tabIndex,
    featureId,
    tabsGroup,
    type: TypeKeys.WORKFLOW_OVERLAY_LOADED,
});

export const updateCurrentEditorState = (featureId: string, data: CurrentStateForEditor): OnUpdateCurrentEditorState => ({
    featureId,
    data,
    type: TypeKeys.UPDATE_CURRENT_EDITOR_STATE,
});

export const questionAssignmentDataLoaded = (
    featureId: string,
    questionsAssignments: DraftAssignments
): OnQuestionAssignmentDataLoadedAction => ({
    featureId,
    questionsAssignments,
    type: TypeKeys.QUESTION_ASSIGNMENT_DATA_LOADED,
});

export const onQuestionAssignmentChange = (
    featureId: string,
    updatedAssignments: QuestionAssignments[]
): OnQuestionAssignmentChangeAction => ({
    featureId,
    updatedAssignments,
    type: TypeKeys.QUESTION_ASSIGNMENT_CHANGE,
});

export const questionAnswerKeysLoaded = (
    featureId: string,
    questionsAnswerKeys: QuestionAnswerKeyCorrection[]
): OnQuestionAnswerKeyLoadedAction => ({
    featureId,
    questionsAnswerKeys,
    type: TypeKeys.QUESTION_ANSWER_KEY_CORRECTION_LOADED,
});

export const questionAnswerKeyChange = (
    featureId: string,
    updatedAnswerKey: QuestionAnswerKeyCorrection
): OnQuestionAnswerKeyChangeAction => ({
    featureId,
    updatedAnswerKey,
    type: TypeKeys.QUESTION_ANSWER_KEY_CORRECTION_CHANGE,
});

export const loadQuestionsForAnswerKeyTab = (
    featureId: string,
): OnLoadQuestionsForAnswerKey => ({
    featureId,
    type: TypeKeys.LOAD_QUESTIONS_FOR_CORRECTION,
});

export const questionsForCorrectionLoaded = (
    featureId: string,
    questions: Question[],
): OnQuestionsForAnswerKeyLoaded => ({
    featureId,
    questions,
    type: TypeKeys.QUESTIONS_FOR_CORRECTION_LOADED,
});

export const selectQuestionForAnswerKeyCorrection = (
    featureId: string,
    questionId: number,
): OnSelectQuestionForAnswerKeyCorrection => ({
    featureId,
    questionId,
    type: TypeKeys.SELECT_QUESTION_FOR_ANSWER_KEY_CORRECTION,
});
